/**
 * @file 图表浮窗面板开关 Hook：打开/关闭、跨游戏 pending、外部 autoSelect 与 Tea onOpenChange。
 */
import { useCallback, useEffect, useRef } from 'react';
import type { RegionSelectValueType } from '@home/components/NewRegionSelect/type';

/** 跨游戏切换时暂存的打开请求 */
interface IPendingOpenRequest {
  /** 待切换 / 待应用的目标游戏 */
  gameCode: string;
  /** 可选：打开后默认高亮的 dashboard value */
  dashboardValue?: string;
  /** true 时数据就绪后立即 confirm，不依赖用户手动关闭 */
  autoSelect: boolean;
}

/** 自定义报表等外部入口传入的自动勾选请求 */
interface IAutoSelectRequest {
  /** 外部指定的游戏 code */
  gameCode: string;
  /** 外部指定的 dashboard value */
  dashboardValue?: string;
}

/** useChartPopoverPanel 入参 */
interface IUseChartPopoverPanelParams {
  /** 浮窗是否已打开 */
  opened: boolean;
  /** 受控开关 setter（来自 useControllableValue） */
  setOpened: (open: boolean) => void;
  /** 当前选中游戏 code */
  selectedGameCode: string;
  /** 无显式传入时的默认游戏 code */
  defaultGameCode: string;
  /** dashboard 数据是否加载中 */
  loading: boolean;
  /** 当前 dashboardOptions 实际所属游戏 code（时序守卫） */
  dashboardOptionsGameCode: string;
  /** 切换游戏并触发 dashboard 请求 */
  handleSelectGame: (gameCode: string) => void;
  /** 外部 autoSelect 请求（生命周期内消费一次） */
  autoSelectRequest: IAutoSelectRequest | null | undefined;
  /** 标记 autoSelect 请求已消费 */
  consumeAutoSelectRequest: () => void;
  /** 反推勾选并写入选区 */
  applySelection: (params: {
    gameCode: string;
    dashboardValue?: string;
    autoSelect: boolean;
  }) => void;
  /** 将当前选区 confirm 给外部 */
  confirmCurrentGame: (value: RegionSelectValueType, gameCode?: string) => void;
  /** 浮窗内当前选区 */
  localSelectValue: RegionSelectValueType;
  /** 清空本地选区 */
  resetLocalSelectValue: () => void;
  /** 触发器 hover 实时态 ref，用于拦截 click toggle 关闭 */
  hoveredRef: React.MutableRefObject<boolean>;
}

/** useChartPopoverPanel 返回值 */
interface IUseChartPopoverPanelResult {
  /** 打开浮窗统一入口 */
  openPanel: (gameCode?: string, dashboardValue?: string, autoSelect?: boolean) => void;
  /** 关闭浮窗统一入口 */
  closePanel: () => void;
  /** 游戏下拉切换回调 */
  handleSwitchGame: (newGameCode: string) => void;
  /** Tea Dropdown onOpenChange 回调 */
  handleOpenChange: (isOpen: boolean) => void;
}

/**
 * 管理图表浮窗的打开、关闭与跨游戏 pending 时序。
 *
 * - `openPanel` / `closePanel` 为唯一开关入口
 * - `pendingOpenRef` 等待目标游戏 dashboard 就绪后再 `applySelection`
 * - 外部 `autoSelectRequest` 双 rAF 后消费，避免与挂载时序冲突
 *
 * @param params - 面板 Hook 入参
 * @returns 开关方法与事件处理器
 */
const useChartPopoverPanel = ({
  opened,
  setOpened,
  selectedGameCode,
  defaultGameCode,
  loading,
  dashboardOptionsGameCode,
  handleSelectGame,
  autoSelectRequest,
  consumeAutoSelectRequest,
  applySelection,
  confirmCurrentGame,
  localSelectValue,
  resetLocalSelectValue,
  hoveredRef,
}: IUseChartPopoverPanelParams): IUseChartPopoverPanelResult => {
  /** 跨游戏打开时暂存请求，等 dashboardOptions 对齐后再应用 */
  const pendingOpenRef = useRef<IPendingOpenRequest | null>(null);

  /**
   * 关闭浮窗：清空 pending 并置 opened=false。
   */
  const closePanel = useCallback(() => {
    pendingOpenRef.current = null;
    setOpened(false);
  }, [setOpened]);

  /**
   * 切换游戏：先 confirm 当前游戏选区，再请求目标游戏数据。
   * @param newGameCode - 用户新选中的游戏 code
   */
  const handleSwitchGame = useCallback((newGameCode: string) => {
    if (newGameCode === selectedGameCode) return;
    confirmCurrentGame(localSelectValue, selectedGameCode);
    pendingOpenRef.current = { gameCode: newGameCode, autoSelect: false };
    handleSelectGame(newGameCode);
    resetLocalSelectValue();
  }, [
    selectedGameCode,
    confirmCurrentGame,
    localSelectValue,
    handleSelectGame,
    resetLocalSelectValue,
  ]);

  /**
   * 打开浮窗：同游戏直接 applySelection，跨游戏写入 pending 并切换请求。
   * @param gameCode - 可选目标游戏
   * @param dashboardValue - 可选默认高亮 dashboard
   * @param autoSelect - 数据就绪后是否立即 confirm
   */
  const openPanel = useCallback((
    gameCode?: string,
    dashboardValue?: string,
    autoSelect = false,
  ) => {
    const targetGameCode = gameCode || selectedGameCode || defaultGameCode;

    if (targetGameCode) {
      if (targetGameCode !== selectedGameCode) {
        pendingOpenRef.current = { gameCode: targetGameCode, dashboardValue, autoSelect };
        handleSelectGame(targetGameCode);
        resetLocalSelectValue();
      } else {
        applySelection({ gameCode: targetGameCode, dashboardValue, autoSelect });
      }
    }

    setOpened(true);
  }, [
    defaultGameCode,
    selectedGameCode,
    handleSelectGame,
    applySelection,
    resetLocalSelectValue,
    setOpened,
  ]);

  /** 外部 autoSelect 消费函数最新引用，供 rAF effect 调用 */
  const runExternalAutoSelectRef = useRef<() => void>(() => {});
  runExternalAutoSelectRef.current = () => {
    if (!autoSelectRequest) return;
    const { gameCode, dashboardValue } = autoSelectRequest;

    if (gameCode !== selectedGameCode) {
      pendingOpenRef.current = { gameCode, dashboardValue, autoSelect: true };
      handleSelectGame(gameCode);
      resetLocalSelectValue();
      consumeAutoSelectRequest();
      return;
    }
    if (loading || dashboardOptionsGameCode !== gameCode) {
      pendingOpenRef.current = { gameCode, dashboardValue, autoSelect: true };
      consumeAutoSelectRequest();
      return;
    }
    applySelection({ gameCode, dashboardValue, autoSelect: true });
    pendingOpenRef.current = null;
    consumeAutoSelectRequest();
  };

  /**
   * 外部 autoSelect：双 rAF 等待 DOM / 数据层就绪后再执行。
   */
  useEffect(() => {
    if (!autoSelectRequest) return;

    let frame2 = 0;
    const frame1 = window.requestAnimationFrame(() => {
      frame2 = window.requestAnimationFrame(() => {
        runExternalAutoSelectRef.current();
      });
    });
    return () => {
      window.cancelAnimationFrame(frame1);
      if (frame2) window.cancelAnimationFrame(frame2);
    };
  }, [autoSelectRequest]);

  /**
   * 跨游戏 pending 兜底：dashboardOptions 对齐目标游戏后应用选区。
   */
  useEffect(() => {
    if (!pendingOpenRef.current) return;
    if (!opened && !pendingOpenRef.current.autoSelect) return;
    if (loading) return;
    const pending = pendingOpenRef.current;
    if (pending.gameCode !== selectedGameCode) return;
    if (dashboardOptionsGameCode !== pending.gameCode) return;

    applySelection({
      gameCode: pending.gameCode,
      dashboardValue: pending.dashboardValue,
      autoSelect: pending.autoSelect,
    });
    pendingOpenRef.current = null;
  }, [opened, loading, selectedGameCode, dashboardOptionsGameCode, applySelection]);

  /**
   * Tea Dropdown 受控 onOpenChange：hover 中拦截关闭，打开时走 openPanel。
   * @param isOpen - Tea 传入的下一开关状态
   */
  const handleOpenChange = useCallback((isOpen: boolean) => {
    if (isOpen) {
      if (!opened) {
        openPanel(selectedGameCode, '', false);
      }
      return;
    }
    if (hoveredRef.current) return;
    closePanel();
  }, [opened, openPanel, selectedGameCode, hoveredRef, closePanel]);

  return {
    openPanel,
    closePanel,
    handleSwitchGame,
    handleOpenChange,
  };
};

export default useChartPopoverPanel;
